import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { faHeartbeat, faTemperatureHigh, faTint, faChair, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend
);

export default function DetallePaciente() {
  const { iduser } = useParams();
  const navigate = useNavigate();
  const [datos, setDatos] = useState([]);
  const [posturas, setPosturas] = useState([]);
  const [paciente, setPaciente] = useState(null);

  useEffect(() => {
    if (!iduser) return;

    const fetchDatos = async () => {
      try {
        const res = await axios.get(`/api/medicos/paciente/datos/${iduser}`);
        const reversed = res.data.signos.reverse();
        setDatos(reversed);
        setPosturas(reversed);
        setPaciente(res.data.paciente);
      } catch (err) {
        console.error('Error al obtener datos del paciente:', err);
      }
    };

    fetchDatos();
    const interval = setInterval(fetchDatos, 5000);
    return () => clearInterval(interval);
  }, [iduser]);

  const labels = datos.map(d => new Date(d.timestamp).toLocaleTimeString('es-MX'));

  const ritmoCardiacoData = {
    labels,
    datasets: [{
      label: 'Ritmo (ppm)',
      data: datos.map(d => d.ritmo_cardiaco),
      borderColor: 'rgb(255, 99, 132)',
      backgroundColor: 'rgba(255, 99, 132, 0.3)',
      tension: 0.3,
      fill: true
    }]
  };

  const temperaturaData = {
    labels,
    datasets: [{
      label: 'Temp (°C)',
      data: datos.map(d => d.temp_corporal),
      backgroundColor: 'rgba(54, 162, 235, 0.6)',
      borderColor: 'rgb(54, 162, 235)',
      borderWidth: 1
    }]
  };

  const spo2Data = {
    labels,
    datasets: [{
      label: 'SpO2 (%)',
      data: datos.map(d => d.spo2),
      backgroundColor: 'rgba(75, 192, 192, 0.6)',
      borderColor: 'rgb(75, 192, 192)',
      borderWidth: 1
    }]
  };

  const opcionesGraficas = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: { mode: 'index', intersect: false }
    },
    scales: {
      y: { beginAtZero: true }
    }
  };

  const ultimos = posturas.slice(-50);
  const conteo = ultimos.reduce((acc, curr) => {
    if (curr.postura === 'mala') acc.mala++;
    else acc.buena++;
    return acc;
  }, { buena: 0, mala: 0 });

  const posturaBarData = {
    labels: ['Buena Postura', 'Mala Postura'],
    datasets: [
      {
        label: 'Cantidad',
        data: [conteo.buena, conteo.mala],
        backgroundColor: ['#22c55e', '#b91c1c'],
        borderRadius: 6,
      },
    ],
  };

  return (
    <div className="relative max-w-[1200px] mx-auto p-8">
      {/* Botón de regresar */}
      <button
        onClick={() => navigate('/doctor/pacientes')}
        className="absolute -top-2 left-4 bg-white/80 hover:bg-white text-gray-800 px-3 py-2 rounded-full shadow-md transition"
      >
        <FontAwesomeIcon icon={faArrowLeft} />
      </button>

      <div className="backdrop-blur-md bg-white/30 shadow-md rounded-2xl p-6 mt-6">
        {paciente && (
          <div className="mb-6 bg-white/70 p-4 rounded-lg shadow">
            <h2 className="text-xl font-bold mb-2">Información del Paciente</h2>
            <p><strong>Nombre:</strong> {paciente.name} {paciente.apellidos}</p>
            <p><strong>Email:</strong> {paciente.email}</p>
            <p><strong>Edad:</strong> {paciente.edad || 'N/A'}</p>
            <p><strong>Teléfono:</strong> {paciente.telefono || 'N/A'}</p>
            <p><strong>CP:</strong> {paciente.cp || 'N/A'}</p>
            <p><strong>Municipio:</strong> {paciente.municipio || 'N/A'}</p>
            <p><strong>Estado:</strong> {paciente.estado || 'N/A'}</p>
            <p><strong>País:</strong> {paciente.pais || 'N/A'}</p>
            <p><strong>Fecha de Asignación:</strong> {new Date(paciente.fecha_asignacion).toLocaleDateString()}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Ritmo Cardiaco */}
          <div className="bg-white/80 p-4 rounded-xl shadow-md">
            <div className="flex items-center gap-2 mb-2 text-red-600 font-semibold text-md">
              <FontAwesomeIcon icon={faHeartbeat} />
              <span>Ritmo Cardíaco</span>
            </div>
            <Line data={ritmoCardiacoData} options={opcionesGraficas} />
          </div>

          {/* Temperatura */}
          <div className="bg-white/80 p-4 rounded-xl shadow-md">
            <div className="flex items-center gap-2 mb-2 text-blue-600 font-semibold text-md">
              <FontAwesomeIcon icon={faTemperatureHigh} />
              <span>Temperatura</span>
            </div>
            <Bar data={temperaturaData} options={opcionesGraficas} />
          </div>

          {/* SpO2 */}
          <div className="bg-white/80 p-4 rounded-xl shadow-md">
            <div className="flex items-center gap-2 mb-2 text-teal-600 font-semibold text-md">
              <FontAwesomeIcon icon={faTint} />
              <span>Oxígeno en Sangre</span>
            </div>
            <Bar data={spo2Data} options={opcionesGraficas} />
          </div>

          {/* Postura */}
          <div className="bg-white/80 p-4 rounded-xl shadow-md">
            <div className="flex items-center gap-2 mb-2 text-purple-600 font-semibold text-md">
              <FontAwesomeIcon icon={faChair} />
              <span>Postura</span>
            </div>
            <Bar data={posturaBarData} options={opcionesGraficas} />
          </div>
        </div>
      </div>
    </div>
  );
}
